import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import useApiContext from 'hooks/useApiContext';
import { iPublicOrder } from 'services/Ticketing/Ticketing.type';

const TEXT = {
  fr: {
    thanks: "C'est confirmé !",
    sent: (e: string) =>
      `Votre e-billet a été envoyé à ${e}. Vous pouvez aussi le télécharger ici.`,
    download: 'Télécharger le billet (PDF)',
    pending: 'Paiement en cours de confirmation…',
    pendingHelp:
      'Cette page se met à jour toute seule. Si vous avez fermé la page de paiement, vous pouvez recommencer.',
    cancelled: "Cette commande n'a pas abouti.",
    retry: "Recommencer l'inscription",
    order: 'Commande',
    show: "Présentez ce QR code à l'entrée.",
    notFound: 'Commande introuvable.',
  },
  en: {
    thanks: "You're all set!",
    sent: (e: string) =>
      `Your e-ticket was sent to ${e}. You can also download it here.`,
    download: 'Download the ticket (PDF)',
    pending: 'Confirming your payment…',
    pendingHelp:
      'This page updates by itself. If you closed the payment page, you can start again.',
    cancelled: 'This order did not go through.',
    retry: 'Register again',
    order: 'Order',
    show: 'Show this QR code at the entrance.',
    notFound: 'Order not found.',
  },
};

/** Confirmation page after registration / payment. */
export default function PublicOrder(): JSX.Element {
  const { slug = '', reference = '' } = useParams();
  const [search] = useSearchParams();
  const company = window.location.pathname.split('/')[1];
  const { i18n } = useTranslation();
  const L = TEXT[i18n.language?.startsWith('en') ? 'en' : 'fr'];
  const { ticketingRepository } = useApiContext();
  const [d, setD] = useState<iPublicOrder | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let timer = 0;
    const load = (): void => {
      ticketingRepository
        .getOrder(
          company,
          reference,
          search.get('k') ?? '',
          search.get('session_id')
        )
        .then((r) => {
          setD(r);
          // The Stripe webhook may arrive a few seconds after the visitor
          if (r.order.status === 'pending')
            timer = window.setTimeout(load, 3000);
        })
        .catch(() => setError(true));
    };
    load();
    return () => window.clearTimeout(timer);
  }, [reference]);

  if (error) return <Box sx={{ p: 4, textAlign: 'center' }}>{L.notFound}</Box>;
  if (!d)
    return (
      <Box sx={{ p: 6, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  const settled = d.order.status === 'paid' || d.order.status === 'free';
  const retry = `/${company}/tickets/${slug}`;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f9fafb' }}>
      <Box sx={{ maxWidth: 680, mx: 'auto', px: 2, py: 5 }}>
        <Typography
          sx={{
            fontSize: 13,
            letterSpacing: 2,
            textTransform: 'uppercase',
            color: d.accent,
          }}
        >
          {d.title}
        </Typography>
        {settled ? (
          <>
            <Typography sx={{ fontSize: 30, fontWeight: 700, mt: 1 }}>
              {L.thanks}
            </Typography>
            <Typography sx={{ color: '#374151', mt: 1 }}>
              {L.sent(d.order.email)}
            </Typography>
            {d.tickets.map((t) => (
              <Box
                key={t.code}
                sx={{
                  bgcolor: '#fff',
                  border: '1px solid #eee',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  mt: 3,
                }}
              >
                <Box sx={{ height: 8, bgcolor: d.accent }} />
                <Box
                  sx={{
                    p: 3,
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 3,
                    alignItems: 'center',
                  }}
                >
                  <img src={t.qr} alt="QR" width={170} height={170} />
                  <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                    <Typography sx={{ fontSize: 22, fontWeight: 700 }}>
                      {t.name}
                    </Typography>
                    {t.company && <Typography>{t.company}</Typography>}
                    <Typography sx={{ color: '#555', fontSize: 14 }}>
                      {t.type}
                    </Typography>
                    <Typography
                      sx={{ fontFamily: 'monospace', fontSize: 14, mt: 1 }}
                    >
                      {t.code}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: '#888' }}>
                      {L.show}
                    </Typography>
                    <Button
                      href={t.download_url}
                      target="_blank"
                      variant="contained"
                      sx={{
                        mt: 2,
                        bgcolor: d.accent,
                        textTransform: 'none',
                        '&:hover': { bgcolor: d.accent },
                      }}
                    >
                      {L.download}
                    </Button>
                  </Box>
                </Box>
              </Box>
            ))}
          </>
        ) : d.order.status === 'pending' ? (
          <>
            <Typography
              sx={{
                fontSize: 24,
                fontWeight: 700,
                mt: 1,
                display: 'flex',
                gap: 1.5,
                alignItems: 'center',
              }}
            >
              <CircularProgress size={22} />
              {L.pending}
            </Typography>
            <Typography sx={{ color: '#555', mt: 1 }}>
              {L.pendingHelp}
            </Typography>
            <a href={retry} style={{ display: 'inline-block', marginTop: 16 }}>
              {L.retry}
            </a>
          </>
        ) : (
          <>
            <Typography sx={{ fontSize: 24, fontWeight: 700, mt: 1 }}>
              {L.cancelled}
            </Typography>
            <Button
              href={retry}
              variant="contained"
              sx={{ mt: 2, bgcolor: d.accent, textTransform: 'none' }}
            >
              {L.retry}
            </Button>
          </>
        )}
        <Typography sx={{ fontSize: 12, color: '#aaa', mt: 4 }}>
          {L.order} {d.order.reference}
        </Typography>
      </Box>
    </Box>
  );
}
